import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { authApi } from '@/api/auth';
import type { User } from '@/types';

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null);
  const token = ref<string | null>(null);

  const isAuthenticated = computed(() => !!token.value && !!user.value);

  const hasPermission = (permission: string): boolean => {
    if (!user.value || !user.value.roles) return false;
    
    const permissions = new Set<string>();
    for (const role of user.value.roles) {
      if (!role || role.isActive === false) continue;
      if (role.permissions && Array.isArray(role.permissions)) {
        role.permissions.forEach(p => permissions.add(p));
      }
    }
    
    return permissions.has(permission) || permissions.has('*');
  };

  const login = async (credentials: {
    email: string;
    password: string;
    mfaToken?: string;
  }) => {
    const response = await authApi.login(credentials);
    
    if (response.success && response.data) {
      user.value = response.data.user;
      token.value = response.data.token;
      
      // Persist to localStorage
      localStorage.setItem('thor_user', JSON.stringify(user.value));
      localStorage.setItem('thor_token', token.value);
    }
    
    return response;
  };

  const logout = () => {
    user.value = null;
    token.value = null;
    
    localStorage.removeItem('thor_user');
    localStorage.removeItem('thor_token');
  };

  const restoreAuth = () => {
    const storedUser = localStorage.getItem('thor_user');
    const storedToken = localStorage.getItem('thor_token');
    
    if (storedUser && storedToken) {
      user.value = JSON.parse(storedUser);
      token.value = storedToken;
    }
  };

  const switchRole = async (roleId: string) => {
    await authApi.switchRole(roleId);
    
    // Refresh user data
    const response = await authApi.getCurrentUser();
    if (response.success && response.data) {
      user.value = response.data;
      localStorage.setItem('thor_user', JSON.stringify(user.value));
    }
  };

  return {
    user,
    token,
    isAuthenticated,
    hasPermission,
    login,
    logout,
    restoreAuth,
    switchRole,
  };
});
